/**
 * What changed between two versions of a page (usually the live version and
 * the draft), for the publish sheet and the review view.
 *
 * Section ids can't be used to match sections: the server gives every section
 * a new id on each save. So sections are matched by content, per slot:
 *   1. identical sections that keep their order (longest common subsequence),
 *   2. identical sections that moved,
 *   3. sections of the same kind (same widget, same custom widget or preset)
 *      in order: changed,
 *   4. the rest: added or removed.
 */
import { propsSignature } from './propsSignature';

export type DiffComponent = {
  _id?: string;
  widgetId: string;
  presetId?: string | null;
  props?: Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  subcomponents?: Array<DiffComponent | null>;
};
type Slot = { slotId: string; components: Array<DiffComponent | null> };
export type DiffPage = {
  title?: string;
  path?: string;
  canonicalRel?: string | null;
  metadata?: Array<{ key: string; value: string }> | null;
  template?: { templateId?: string; version?: string; props?: Record<string, any> } | null; // eslint-disable-line @typescript-eslint/no-explicit-any
  slots?: Slot[] | null;
};

export type SectionStatus = 'added' | 'removed' | 'changed' | 'moved';
export type SectionChange = {
  status: SectionStatus;
  slotId: string;
  /** The section in the newer version (removed: in the older one). */
  component: DiffComponent;
  before?: DiffComponent;
  /** Position in the newer version (removed: in the older one). */
  index: number;
};
export type SettingChange = { field: string; label: string; before: string; after: string };
export type PageDiff = { sections: SectionChange[]; settings: SettingChange[]; unchanged: number };

const sectionSig = (c: DiffComponent | null): string => (c
  ? JSON.stringify([c.widgetId, c.presetId ? String(c.presetId) : null, propsSignature(c.props), (c.subcomponents || []).map(sectionSig)])
  : 'null');

// "Same kind": a change to it reads as an edit rather than a swap.
const kindOf = (c: DiffComponent): string => {
  if (c.presetId) return `preset:${String(c.presetId)}`;
  if (c.widgetId === 'CustomWidget' && c.props && c.props.customWidgetName) return `custom:${c.props.customWidgetName}`;
  return `widget:${c.widgetId}`;
};

/** Pairs of indexes (a, b) of the longest common subsequence of two lists. */
const lcsPairs = (a: string[], b: string[]): Array<[number, number]> => {
  const table: number[][] = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      table[i][j] = a[i] === b[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }
  const pairs: Array<[number, number]> = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) { pairs.push([i, j]); i++; j++; } else if (table[i + 1][j] >= table[i][j + 1]) i++; else j++;
  }
  return pairs;
};

const diffSlot = (slotId: string, before: Array<DiffComponent | null>, after: Array<DiffComponent | null>) => {
  const a = before.map((c, i) => ({ c, i })).filter(x => x.c) as Array<{ c: DiffComponent; i: number }>;
  const b = after.map((c, i) => ({ c, i })).filter(x => x.c) as Array<{ c: DiffComponent; i: number }>;
  const aSig = a.map(x => sectionSig(x.c));
  const bSig = b.map(x => sectionSig(x.c));
  const usedA = new Set<number>();
  const usedB = new Set<number>();
  const changes: SectionChange[] = [];
  let unchanged = 0;

  lcsPairs(aSig, bSig).forEach(([i, j]) => { usedA.add(i); usedB.add(j); unchanged += 1; });

  // Identical, but not in the same order.
  b.forEach((x, j) => {
    if (usedB.has(j)) return;
    const i = aSig.findIndex((s, k) => !usedA.has(k) && s === bSig[j]);
    if (i < 0) return;
    usedA.add(i); usedB.add(j);
    changes.push({ status: 'moved', slotId, component: x.c, before: a[i].c, index: x.i });
  });

  // Same kind, different content.
  b.forEach((x, j) => {
    if (usedB.has(j)) return;
    const i = a.findIndex((y, k) => !usedA.has(k) && kindOf(y.c) === kindOf(x.c));
    if (i < 0) return;
    usedA.add(i); usedB.add(j);
    changes.push({ status: 'changed', slotId, component: x.c, before: a[i].c, index: x.i });
  });

  b.forEach((x, j) => { if (!usedB.has(j)) changes.push({ status: 'added', slotId, component: x.c, index: x.i }); });
  a.forEach((y, i) => { if (!usedA.has(i)) changes.push({ status: 'removed', slotId, component: y.c, index: y.i }); });
  return { changes, unchanged };
};

// Page order of the template slots; other slots follow by name.
const SLOT_ORDER = ['menu-bar', 'content', 'footer'];

const meta = (page: DiffPage) => new Map((page.metadata || []).map(m => [m.key, m.value || '']));

export const diffPages = (before: DiffPage | null | undefined, after: DiffPage): PageDiff => {
  const old = before || { slots: [] };
  const slotIds = Array.from(new Set([...(old.slots || []), ...(after.slots || [])].map(s => s.slotId)));
  const sections: SectionChange[] = [];
  let unchanged = 0;
  slotIds.forEach((slotId) => {
    const a = ((old.slots || []).find(s => s.slotId === slotId) || { components: [] }).components || [];
    const b = ((after.slots || []).find(s => s.slotId === slotId) || { components: [] }).components || [];
    const r = diffSlot(slotId, a, b);
    sections.push(...r.changes);
    unchanged += r.unchanged;
  });
  const order: Record<SectionStatus, number> = { added: 0, changed: 1, moved: 2, removed: 3 };
  const slotRank = (id: string) => { const r = SLOT_ORDER.indexOf(id); return r === -1 ? SLOT_ORDER.length : r; };
  sections.sort((x, y) => slotRank(x.slotId) - slotRank(y.slotId)
    || (x.slotId < y.slotId ? -1 : x.slotId > y.slotId ? 1 : 0)
    || x.index - y.index
    || order[x.status] - order[y.status]);

  const settings: SettingChange[] = [];
  const field = (key: string, label: string, x?: string | null, y?: string | null) => {
    if ((x || '') !== (y || '')) settings.push({ field: key, label, before: x || '', after: y || '' });
  };
  if (before) {
    field('title', 'Title', old.title, after.title);
    field('path', 'Path', old.path, after.path);
    field('canonicalRel', 'Canonical URL', old.canonicalRel, after.canonicalRel);
    const ma = meta(old);
    const mb = meta(after);
    Array.from(new Set(Array.from(ma.keys()).concat(Array.from(mb.keys())))).forEach(k => field(`meta:${k}`, k, ma.get(k), mb.get(k)));
    const ta = old.template || {};
    const tb = after.template || {};
    field('template.version', 'Template version', ta.version, tb.version);
    if (propsSignature(ta.props) !== propsSignature(tb.props)) settings.push({ field: 'template.props', label: 'Template settings', before: '', after: '' });
  }
  return { sections, settings, unchanged };
};

const VERB: Record<SectionStatus, string> = { added: 'Added', changed: 'Changed', moved: 'Moved', removed: 'Removed' };

/** A one-line version note from a diff, e.g. "Added Home News; changed Contact Us; new title." */
export const suggestNote = (diff: PageDiff, nameOf: (c: DiffComponent) => string): string => {
  const parts: string[] = [];
  (['added', 'changed', 'moved', 'removed'] as SectionStatus[]).forEach((status) => {
    const names = Array.from(new Set(diff.sections.filter(s => s.status === status).map(s => nameOf(s.component))));
    if (!names.length) return;
    const shown = names.length > 3 ? `${names.slice(0, 3).join(', ')} and ${names.length - 3} more` : names.join(', ');
    parts.push(`${parts.length ? VERB[status].toLowerCase() : VERB[status]} ${shown}`);
  });
  const settings = diff.settings.map(s => s.label.toLowerCase());
  if (settings.length) parts.push(`${parts.length ? 'updated' : 'Updated'} ${settings.slice(0, 3).join(', ')}${settings.length > 3 ? ' and more' : ''}`);
  return parts.length ? `${parts.join('; ')}.` : '';
};
